// Marking an area of ground: the INPUT half of territory.ts's "Claim area"
// and "Mark contested area".
//
// A report of a front rarely comes in the shape of a district, so there are
// four ways to say which ground, mixed freely in one gesture:
//
//   ➰ DRAW AROUND — press, drag a line around the ground, let go: the line
//            closes itself and the area goes straight to the form. The way a
//            person marks ground on a paper map, and the default. It does not
//            need to meet the receiving side's line exactly — overlapping its
//            own ground merges — so only the NEW edge has to be drawn well.
//   ✎ CORNERS — click the corners. With ⌁ Snap each corner lands on the line
//            under it (a territory's outline, an admin boundary, a road), and
//            with ⇝ Follow two snapped corners in a row are joined ALONG the
//            lines between them instead of by a straight edge — an area
//            bounded by a road and two district edges is a handful of clicks.
//   ◉ PAINT — drag a brush. A stroke is kept as its path and a radius in
//            METRES, fixed at the zoom it was painted at and buffered on the
//            server, so the ground it covers does not change as the map zooms.
//   ▣ UNITS — click admin units at the current level to toggle them in:
//            districts, and sub-districts where the detailed set has them.
//
// Nothing is written here. Finish hands the inputs to territory.ts, which asks
// the server for the plan (POST /api/territory/area with dryRun: the outline
// clipped to one country, fitted to units if asked, and what it takes from
// whom) and applies it from the form.
//
// Pointer events for the brush (a finger never raises MapLibre's mouse
// events), the draw bar for every way out (drawbar.ts), and the map's clicks
// held for the whole gesture so a click on a territory does not open its card.

import * as maplibregl from 'maplibre-gl';
import type { EntityDto } from './api';
import { get } from './api';
import { showDrawBar } from './drawbar';
import { fmtDistance } from './geo';
import { createGuideSet } from './guides';
import { toast } from './hud';
import {
  buildSnapIndex, nearestSnap, traceNetwork, type Guide, type Pt, type SnapHit, type SnapIndex,
} from './snap';
import { flatRing, holdMapClicks, ringsOf, SNAP_MAX_PTS, SNAP_PX } from './territoryedit';

export const SRC_AREA = 'terr-area';
export const SRC_AREA_LIVE = 'terr-area-live';
export const L_AREA_STROKE = 'terr-area-stroke';
export const L_AREA_FILL = 'terr-area-fill';
export const L_AREA_LINE = 'terr-area-line';
export const L_AREA_LIVE_STROKE = 'terr-area-live-stroke';
export const L_AREA_BAND = 'terr-area-band';
export const L_AREA_CORNER = 'terr-area-corner';
export const L_AREA_SNAP = 'terr-area-snap';
/** Brush sizes, in pixels at the zoom a stroke is painted at. */
export const PAINT_RADII = [12, 24, 40, 64, 100];
/** MapLibre's world is 512 px wide at zoom 0. */
const WORLD_M = 40_075_016.686;

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

/** A freehand outline shorter than this across (px) is a slip, not an area. */
export const LASSO_MIN_PX = 12;
/** How far (px) a freehand outline may stray from the hand's path when its
 *  points are thinned: under the line's own width. */
export const LASSO_TOLERANCE_PX = 1.5;

export interface AreaStroke { line: Pt[]; radiusM: number }

/** What the operator marked — every part optional, at least one present. */
export interface AreaInput {
  /** Clicked corners and drawn-around outlines: a MultiPolygon when there
   *  are several. They may overlap and a hand-drawn one may cross itself;
   *  the server reads them as the ground they enclose. */
  polygon?: GeoJSON.Polygon | GeoJSON.MultiPolygon;
  strokes?: AreaStroke[];
  units?: string[];
  /** The admin level the guides were at: what "fit to boundaries" fits to. */
  level: number;
  /** The country the gesture started in, if known. */
  iso: string;
}

export type AreaMode = 'lasso' | 'draw' | 'paint' | 'units';

export interface AreaOpts {
  title: string;
  iso?: string;
  /** Territory outlines to snap corners to. */
  outlines?: EntityDto[];
  /** The mode the gesture opens in (default: draw around). */
  mode?: AreaMode;
  /** One more sentence for the bar while drawing an outline. */
  hint?: string;
  /** Ground already marked — "Add more" draws again over the plan — shown
   *  in the plan's red while the gesture runs. */
  base?: GeoJSON.Polygon | GeoJSON.MultiPolygon;
}

/** A territory the plan takes ground from (POST /api/territory/area). */
export interface AreaTake {
  id: string; name: string | null; faction: string | null; status: string | null;
  contestedBy: string | null; km2: number;
}

interface UnitDto {
  id: string; level: number; name: string | null; iso: string; source: string;
  geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon;
}

/** Ground metres per CSS pixel at a zoom and latitude. */
export function metersPerPixel(zoom: number, lat: number): number {
  return (WORLD_M * Math.cos((lat * Math.PI) / 180)) / (512 * 2 ** zoom);
}

/** A stroke's drawn width at zoom 0 — the exponential zoom ramp in the layer
 *  doubles it per zoom level, so it covers the same ground at every zoom. */
export function strokeWidthZ0(radiusM: number, lat: number): number {
  return (2 * radiusM) / metersPerPixel(0, lat);
}

/** The drawn corners (each with the run Follow traced up to it) as one closed
 *  ring, repeated points dropped. Null below three distinct corners. */
export function ringOf(corners: { pt: Pt; run: Pt[] }[], closing: Pt[] = []): Pt[] | null {
  const pts: Pt[] = [];
  const push = (p: Pt): void => {
    const last = pts[pts.length - 1];
    if (!last || last[0] !== p[0] || last[1] !== p[1]) pts.push(p);
  };
  for (const c of corners) {
    for (const p of c.run) push(p);
    push(c.pt);
  }
  for (const p of closing) push(p);
  const first = pts[0];
  const last = pts[pts.length - 1];
  if (pts.length > 1 && first && last && first[0] === last[0] && first[1] === last[1]) pts.pop();
  if (new Set(pts.map(p => `${p[0]},${p[1]}`)).size < 3) return null;
  return [...pts, pts[0]!];
}

/** Douglas–Peucker over screen points: the indices worth keeping of a
 *  freehand line, every dropped point within `tol` px of what is left. A hand
 *  puts down hundreds of points a stroke; the outline needs its corners. */
export function simplifyPath(pts: { x: number; y: number }[], tol: number): number[] {
  const n = pts.length;
  if (n <= 2) return pts.map((_, i) => i);
  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;
  const stack: [number, number][] = [[0, n - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    const A = pts[a]!;
    const B = pts[b]!;
    const dx = B.x - A.x;
    const dy = B.y - A.y;
    const len = Math.hypot(dx, dy);
    let worst = -1;
    let at = -1;
    for (let i = a + 1; i < b; i++) {
      const P = pts[i]!;
      const d = len === 0
        ? Math.hypot(P.x - A.x, P.y - A.y)
        : Math.abs(dy * (P.x - A.x) - dx * (P.y - A.y)) / len;
      if (d > worst) { worst = d; at = i; }
    }
    if (at >= 0 && worst > tol) {
      keep[at] = 1;
      stack.push([a, at], [at, b]);
    }
  }
  const out: number[] = [];
  keep.forEach((k, i) => { if (k) out.push(i); });
  return out;
}

const polygonsOf = (g: GeoJSON.Polygon | GeoJSON.MultiPolygon | undefined): GeoJSON.Position[][][] =>
  (!g ? [] : g.type === 'Polygon' ? [g.coordinates] : g.coordinates);

/** One polygon or several, as the API takes them. */
export function polygonOfRings(polys: GeoJSON.Position[][][]):
GeoJSON.Polygon | GeoJSON.MultiPolygon | undefined {
  if (!polys.length) return undefined;
  if (polys.length === 1) return { type: 'Polygon', coordinates: polys[0]! };
  return { type: 'MultiPolygon', coordinates: polys };
}

/** Two gestures' worth of ground as one input: "Add more" draws again and
 *  the plan is made from everything. */
export function mergeAreaInputs(a: AreaInput, b: AreaInput): AreaInput {
  const out: AreaInput = { level: b.level || a.level, iso: a.iso || b.iso };
  const polygon = polygonOfRings([...polygonsOf(a.polygon), ...polygonsOf(b.polygon)]);
  if (polygon) out.polygon = polygon;
  const strokes = [...(a.strokes ?? []), ...(b.strokes ?? [])];
  if (strokes.length) out.strokes = strokes;
  const units = [...new Set([...(a.units ?? []), ...(b.units ?? [])])];
  if (units.length) out.units = units;
  return out;
}

/** Whether a territory the plan overlaps IS the slot the area goes to —
 *  (faction, status[, contested by]), as the server's _same_slot reads it. */
export function sameSlot(t: Pick<AreaTake, 'faction' | 'status' | 'contestedBy'>,
                         faction: string, status: string, contestedBy: string): boolean {
  if ((t.faction ?? '').trim().toLowerCase() !== faction.trim().toLowerCase()) return false;
  if ((t.status ?? 'controlled') !== status) return false;
  if (status !== 'contested') return true;
  return (t.contestedBy ?? '').trim().toLowerCase() === contestedBy.trim().toLowerCase();
}

/** What the slot GAINS: the area less the part of it that territory already
 *  holds — that part just merges. */
export function slotGain(km2: number,
                         takes: Pick<AreaTake, 'faction' | 'status' | 'contestedBy' | 'km2'>[],
                         faction: string, status: string, contestedBy: string): number {
  const own = takes.filter((t) => sameSlot(t, faction, status, contestedBy))
    .reduce((sum, t) => sum + t.km2, 0);
  return Math.max(0, km2 - own);
}

/** Who the form should suggest: the area is held by whoever loses the most of
 *  it, and contested by the other side of the line — the next-biggest loser
 *  with a different holder, else whoever already contests the biggest. */
export function areaDefaults(takes: Pick<AreaTake, 'faction' | 'contestedBy' | 'km2'>[]):
{ faction: string; contestedBy: string } {
  const sorted = [...takes].sort((a, b) => b.km2 - a.km2);
  const holder = sorted[0]?.faction ?? '';
  const other = sorted.find((t) => t.faction && t.faction.toLowerCase() !== holder.toLowerCase());
  return { faction: holder, contestedBy: other?.faction ?? sorted[0]?.contestedBy ?? '' };
}

/** The layers, pure, for the style-spec test. Orange while it is being
 *  gathered; red once it is the server's plan. */
export function areaLayers(): maplibregl.LayerSpecification[] {
  // A stroke's width is ground: `w0` px at zoom 0, doubling per level. zoom
  // may only feed a top-level interpolate, so the ramp is outside and the
  // feature property inside each stop.
  const width: maplibregl.DataDrivenPropertyValueSpecification<number> = [
    'interpolate', ['exponential', 2], ['zoom'],
    0, ['get', 'w0'],
    24, ['*', ['get', 'w0'], 16_777_216],
  ];
  return [
    {
      id: L_AREA_STROKE, type: 'line', source: SRC_AREA,
      filter: ['==', ['get', 'role'], 'stroke'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#ff8f00', 'line-opacity': 0.35, 'line-width': width },
    },
    {
      id: L_AREA_FILL, type: 'fill', source: SRC_AREA,
      filter: ['in', ['get', 'role'], ['literal', ['unit', 'draft', 'preview']]],
      paint: {
        'fill-color': ['match', ['get', 'role'], 'preview', '#ff4d4d', '#ff8f00'],
        'fill-opacity': ['match', ['get', 'role'], 'preview', 0.3, 0.2],
      },
    },
    {
      id: L_AREA_LINE, type: 'line', source: SRC_AREA,
      filter: ['in', ['get', 'role'], ['literal', ['unit', 'draft', 'preview']]],
      layout: { 'line-join': 'round' },
      paint: {
        'line-color': ['match', ['get', 'role'], 'preview', '#ff4d4d', '#ff8f00'],
        'line-width': 2, 'line-dasharray': [2, 1.5],
      },
    },
    {
      id: L_AREA_LIVE_STROKE, type: 'line', source: SRC_AREA_LIVE,
      filter: ['==', ['get', 'role'], 'stroke'],
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: { 'line-color': '#ff8f00', 'line-opacity': 0.35, 'line-width': width },
    },
    {
      id: L_AREA_BAND, type: 'line', source: SRC_AREA_LIVE,
      filter: ['==', ['get', 'role'], 'band'],
      layout: { 'line-join': 'round', 'line-cap': 'round' },
      paint: { 'line-color': '#ff8f00', 'line-width': 2.2 },
    },
    {
      id: L_AREA_CORNER, type: 'circle', source: SRC_AREA_LIVE,
      filter: ['==', ['get', 'role'], 'corner'],
      paint: { 'circle-radius': ['case', ['==', ['get', 'first'], true], 7, 4.5], 'circle-color': '#ffffff',
               'circle-stroke-color': '#ff8f00', 'circle-stroke-width': 2 },
    },
    {
      id: L_AREA_SNAP, type: 'circle', source: SRC_AREA_LIVE,
      filter: ['==', ['get', 'role'], 'snap'],
      paint: { 'circle-radius': 10, 'circle-color': '#7fd1ff', 'circle-opacity': 0.2,
               'circle-stroke-color': '#7fd1ff', 'circle-stroke-width': 2 },
    },
  ];
}

export function installAreaLayers(map: maplibregl.Map): void {
  if (map.getSource(SRC_AREA)) return;
  map.addSource(SRC_AREA, { type: 'geojson', data: EMPTY });
  map.addSource(SRC_AREA_LIVE, { type: 'geojson', data: EMPTY });
  for (const layer of areaLayers()) map.addLayer(layer);
}

const setData = (map: maplibregl.Map, id: string, fc: GeoJSON.FeatureCollection): void => {
  (map.getSource(id) as maplibregl.GeoJSONSource | undefined)?.setData(fc);
};

/** Draw the server's plan; returns what clears it. */
export function showAreaPreview(map: maplibregl.Map,
                                geometry: GeoJSON.Polygon | GeoJSON.MultiPolygon): () => void {
  installAreaLayers(map);
  setData(map, SRC_AREA, { type: 'FeatureCollection', features: [
    { type: 'Feature', properties: { role: 'preview' }, geometry },
  ] });
  let cleared = false;
  return () => {
    if (cleared) return;
    cleared = true;
    setData(map, SRC_AREA, EMPTY);
  };
}

function strokeFeature(line: Pt[], w0: number): GeoJSON.Feature {
  // A dab is one point; a line needs two, and a zero-length one draws nothing.
  const coords = line.length === 1 ? [line[0]!, [line[0]![0] + 1e-9, line[0]![1]] as Pt] : line;
  return { type: 'Feature', properties: { role: 'stroke', w0 },
           geometry: { type: 'LineString', coordinates: coords } };
}

type Mode = AreaMode;
interface Corner { pt: Pt; run: Pt[]; hit: SnapHit | null; index: SnapIndex | null }
type Action = { kind: 'corner' } | { kind: 'stroke' } | { kind: 'lasso' }
  | { kind: 'unit'; unit: UnitDto; added: boolean };

/** Gather an area on the map. Resolves the inputs on Finish, null on Cancel. */
export function captureTerritoryArea(map: maplibregl.Map, opts: AreaOpts): Promise<AreaInput | null> {
  installAreaLayers(map);
  return new Promise((resolve) => {
    const release = holdMapClicks();
    const canvas = map.getCanvas();
    let mode: Mode = opts.mode ?? 'lasso';
    let snapping = false;
    let follow = false;
    let radiusPx = PAINT_RADII[2]!;
    const corners: Corner[] = [];
    const strokes: (AreaStroke & { w0: number })[] = [];
    const lassos: Pt[][] = [];
    let lassoLive: Pt[] | null = null;
    // Space held: a press pans the map instead of drawing (the paint and
    // draw-around modes take the drag for themselves otherwise).
    let spaceHeld = false;
    const units = new Map<string, UnitDto>();
    const actions: Action[] = [];
    let cursor: Pt | null = null;
    let cursorHit: SnapHit | null = null;
    let live: { pts: Pt[]; lat: number; radiusM: number } | null = null;
    let index: SnapIndex | null = null;
    let done = false;
    const wasDragPan = map.dragPan.isEnabled();
    const wasDoubleZoom = map.doubleClickZoom.isEnabled();
    let stopStroke: (() => void) | null = null;
    let brushEl: HTMLDivElement | null = null;
    const outlineRings: Pt[][] = (opts.outlines ?? []).flatMap((e) => ringsOf(e.geometry).map(flatRing));

    const lines = createGuideSet(map, {
      iso: opts.iso ?? '',
      onChange: () => {
        index = null;
        bar.setExtra('admin', { label: lines.levelLabel(), on: !!lines.level() });
        update();
      },
    });

    const ensureIndex = (): SnapIndex => {
      if (index) return index;
      const project = (p: Pt): { x: number; y: number } => {
        const q = map.project(p as [number, number]);
        return { x: q.x, y: q.y };
      };
      const guides: Guide[] = [];
      let total = 0;
      const add = (pts: Pt[], closed: boolean, source: Guide['source']): void => {
        if (pts.length < 2 || total + pts.length > SNAP_MAX_PTS) return;
        total += pts.length;
        guides.push({ pts, screen: pts.map(project), closed, source });
      };
      for (const l of lines.lines()) add(l.pts, false, l.source);
      for (const r of outlineRings) add(r, true, 'territory');
      index = buildSnapIndex(guides);
      return index;
    };
    const snapAt = (x: number, y: number): SnapHit | null =>
      (snapping ? nearestSnap(ensureIndex(), { x, y }, SNAP_PX) : null);
    /** A corner's hit in the CURRENT index: the camera may have moved since. */
    const hitFor = (c: Corner): SnapHit | null => {
      if (!c.hit) return null;
      const idx = ensureIndex();
      if (c.index === idx) return c.hit;
      const q = map.project(c.pt as [number, number]);
      return nearestSnap(idx, { x: q.x, y: q.y }, 1.5);
    };
    const joined = (a: SnapHit | null, b: SnapHit | null): Pt[] | null => {
      if (!follow || !a || !b) return null;
      // Territory outlines only at the two ends: a country's worth of
      // vertices is no network to route across.
      return traceNetwork(ensureIndex().guides, a, b, (g) => g.source !== 'territory');
    };

    const paintStatic = (): void => {
      const feats: GeoJSON.Feature[] = [];
      if (opts.base) feats.push({ type: 'Feature', properties: { role: 'preview' }, geometry: opts.base });
      for (const u of units.values()) {
        feats.push({ type: 'Feature', properties: { role: 'unit' }, geometry: u.geometry });
      }
      for (const s of strokes) feats.push(strokeFeature(s.line, s.w0));
      for (const l of lassos) {
        feats.push({ type: 'Feature', properties: { role: 'draft' },
                     geometry: { type: 'Polygon', coordinates: [l] } });
      }
      const ring = ringOf(corners);
      if (ring) {
        feats.push({ type: 'Feature', properties: { role: 'draft' },
                     geometry: { type: 'Polygon', coordinates: [ring] } });
      }
      setData(map, SRC_AREA, { type: 'FeatureCollection', features: feats });
    };
    const paintLive = (): void => {
      const feats: GeoJSON.Feature[] = [];
      if (live) feats.push(strokeFeature(live.pts, strokeWidthZ0(live.radiusM, live.lat)));
      if (lassoLive && lassoLive.length >= 2) {
        // Closed back to its start as it is drawn: what letting go would mark.
        feats.push({ type: 'Feature', properties: { role: 'band' },
                     geometry: { type: 'LineString', coordinates: [...lassoLive, lassoLive[0]!] } });
      }
      if (mode === 'draw') {
        const path = corners.flatMap((c) => [...c.run, c.pt]);
        if (path.length) {
          const band: Pt[] = cursor ? [...path, cursor] : path;
          if (band.length >= 2) {
            feats.push({ type: 'Feature', properties: { role: 'band' },
                         geometry: { type: 'LineString', coordinates: band } });
          }
        }
        for (const c of corners) {
          feats.push({ type: 'Feature', properties: { role: 'corner', first: c === corners[0] },
                       geometry: { type: 'Point', coordinates: c.pt } });
        }
        if (cursorHit) {
          feats.push({ type: 'Feature', properties: { role: 'snap' },
                       geometry: { type: 'Point', coordinates: cursorHit.pos } });
        }
      }
      setData(map, SRC_AREA_LIVE, { type: 'FeatureCollection', features: feats });
    };

    const extra = opts.hint ? ` ${opts.hint}` : '';
    const hint = (): string => {
      if (mode === 'lasso') return `Press and drag a line all the way around the ground, then let go — it closes itself.${extra} Hold Space and drag to pan, scroll to zoom.`;
      if (mode === 'draw') return `${snapping ? 'Click to add corners; Alt skips snapping.' : 'Click to add corners. Drag the map to pan.'} Click the first corner to close, or Preview area.${extra} Backspace undoes.`;
      if (mode === 'paint') {
        const m = radiusPx * metersPerPixel(map.getZoom(), map.getCenter().lat);
        return `Drag to paint · brush ${radiusPx}px ≈ ${fmtDistance(m)} radius`;
      }
      return `Click districts to add or remove them (ADM${lines.level() || 2}), then Preview area.`;
    };
    const update = (): void => {
      const parts: string[] = [];
      if (lassos.length) parts.push(`${lassos.length} outline${lassos.length === 1 ? '' : 's'}`);
      if (corners.length) parts.push(`${corners.length} corner${corners.length === 1 ? '' : 's'}`);
      if (strokes.length) parts.push(`${strokes.length} stroke${strokes.length === 1 ? '' : 's'}`);
      if (units.size) parts.push(`${units.size} unit${units.size === 1 ? '' : 's'}`);
      const readout = [parts.join(' · ') || 'No area selected', lines.status()].filter(Boolean).join(' · ');
      bar.setHint(hint());
      const incomplete = corners.length > 0 && !ringOf(corners);
      bar.update(actions.length, incomplete ? `${readout} · Outline needs 3 distinct corners` : readout,
        !incomplete && (!!ringOf(corners) || lassos.length > 0 || strokes.length > 0 || units.size > 0));
    };

    const placeBrush = (x: number, y: number): void => {
      if (mode !== 'paint') { brushEl?.remove(); brushEl = null; return; }
      if (!brushEl) {
        brushEl = document.createElement('div');
        brushEl.className = 'terr-brush';
        document.body.appendChild(brushEl);
      }
      const rect = canvas.getBoundingClientRect();
      brushEl.style.width = `${radiusPx * 2}px`;
      brushEl.style.height = `${radiusPx * 2}px`;
      brushEl.style.left = `${rect.left + x - radiusPx}px`;
      brushEl.style.top = `${rect.top + y - radiusPx}px`;
    };

    const setMode = (m: Mode): void => {
      stopStroke?.();
      mode = m;
      bar.setExtra('radius', { hidden: m !== 'paint' });
      for (const k of ['lasso', 'draw', 'paint', 'units'] as Mode[]) bar.setExtra(k, { on: k === m });
      cursor = null;
      cursorHit = null;
      if (m !== 'paint') { brushEl?.remove(); brushEl = null; }
      if (m === 'units' && !lines.level()) lines.setLevel(2);
      canvas.style.cursor = spaceHeld ? 'grab' : 'crosshair';
      paintLive();
      update();
    };
    const stepRadius = (dir: 1 | -1, wrap: boolean): void => {
      const i = Math.max(0, PAINT_RADII.indexOf(radiusPx));
      const n = PAINT_RADII.length;
      radiusPx = PAINT_RADII[wrap ? (i + dir + n) % n : Math.max(0, Math.min(n - 1, i + dir))]!;
      bar.setExtra('radius', { label: `◌ ${radiusPx}px` });
      update();
    };

    const toggleUnit = async (lon: number, lat: number): Promise<void> => {
      const level = lines.level() || 2;
      let unit: UnitDto;
      try {
        unit = await get<UnitDto>(
          `/api/boundaries/unit?lon=${lon.toFixed(6)}&lat=${lat.toFixed(6)}&level=${level}`);
      } catch (err) {
        toast(err instanceof Error ? err.message : `No ADM${level} unit there.`);
        return;
      }
      if (done) return;
      const had = units.has(unit.id);
      if (had) units.delete(unit.id); else units.set(unit.id, unit);
      actions.push({ kind: 'unit', unit, added: !had });
      paintStatic();
      update();
      toast(`${had ? 'Removed' : 'Added'} ${unit.name ?? 'a unit'} (ADM${unit.level}, ${unit.source}).`);
    };

    const onClick = (e: maplibregl.MapMouseEvent): void => {
      if (done || mode === 'paint' || mode === 'lasso' || e.originalEvent.detail > 1) return;
      if (mode === 'draw' && corners.length >= 3) {
        const first = map.project(corners[0]!.pt);
        if (Math.hypot(e.point.x - first.x, e.point.y - first.y) <= 12) { finish(); return; }
      }
      if (mode === 'units') { void toggleUnit(e.lngLat.lng, e.lngLat.lat); return; }
      const alt = (e.originalEvent as MouseEvent).altKey;
      const hit = alt ? null : snapAt(e.point.x, e.point.y);
      const pt: Pt = hit ? [hit.pos[0]!, hit.pos[1]!] : [e.lngLat.lng, e.lngLat.lat];
      let run: Pt[] = [];
      const prev = corners[corners.length - 1];
      if (prev && hit) {
        const path = joined(hitFor(prev), hit);
        if (path && path.length > 2) run = path.slice(1, -1);
        else if (follow && prev.hit && !path) {
          toast('Those corners are not joined along the lines in view — a straight edge instead.');
        }
      }
      if (prev && prev.pt[0] === pt[0] && prev.pt[1] === pt[1]) return;
      corners.push({ pt, run, hit, index: hit ? index : null });
      actions.push({ kind: 'corner' });
      paintStatic();
      paintLive();
      update();
    };
    const onMove = (e: maplibregl.MapMouseEvent): void => {
      if (mode === 'paint') { if (!live) placeBrush(e.point.x, e.point.y); return; }
      if (mode !== 'draw') return;
      const alt = (e.originalEvent as MouseEvent).altKey;
      cursorHit = alt ? null : snapAt(e.point.x, e.point.y);
      cursor = cursorHit ? [cursorHit.pos[0]!, cursorHit.pos[1]!] : [e.lngLat.lng, e.lngLat.lat];
      if (corners.length || cursorHit) paintLive();
    };
    const onDown = (ev: PointerEvent): void => {
      if (done || stopStroke || ev.button !== 0 || spaceHeld) return;
      if (mode === 'paint') startPaint(ev);
      else if (mode === 'lasso') startLasso(ev);
    };
    /** Draw around: the pointer's path, kept as ground as it is drawn (a
     *  scroll mid-drag must not move what was already drawn), thinned in
     *  screen space on release, closed, and handed straight to the form. */
    const startLasso = (ev: PointerEvent): void => {
      ev.preventDefault();
      ev.stopPropagation();
      canvas.setPointerCapture(ev.pointerId);
      map.dragPan.disable();
      const rect = canvas.getBoundingClientRect();
      const at = (p: PointerEvent): [number, number] => [p.clientX - rect.left, p.clientY - rect.top];
      const toLL = (p: [number, number]): Pt => {
        const q = map.unproject(p);
        return [q.lng, q.lat];
      };
      let lastPx = at(ev);
      lassoLive = [toLL(lastPx)];
      paintLive();
      const move = (m: PointerEvent): void => {
        if (m.pointerId !== ev.pointerId || !lassoLive) return;
        const p = at(m);
        if (Math.hypot(p[0] - lastPx[0], p[1] - lastPx[1]) < 3) return;
        lastPx = p;
        lassoLive.push(toLL(p));
        paintLive();
      };
      stopStroke = () => {
        canvas.removeEventListener('pointermove', move);
        canvas.removeEventListener('pointerup', up);
        canvas.removeEventListener('pointercancel', up);
        if (canvas.hasPointerCapture(ev.pointerId)) canvas.releasePointerCapture(ev.pointerId);
        if (wasDragPan) map.dragPan.enable();
        lassoLive = null;
        stopStroke = null;
      };
      const up = (u: PointerEvent): void => {
        if (u.pointerId !== ev.pointerId) return;
        if (u.type !== 'pointercancel') move(u);
        const path = lassoLive ?? [];
        stopStroke?.();
        if (done || u.type === 'pointercancel') { paintLive(); return; }
        const screen = path.map((p) => map.project(p as [number, number]));
        const xs = screen.map((q) => q.x);
        const ys = screen.map((q) => q.y);
        const span = screen.length
          ? Math.max(Math.max(...xs) - Math.min(...xs), Math.max(...ys) - Math.min(...ys)) : 0;
        const ring = span >= LASSO_MIN_PX
          ? ringOf(simplifyPath(screen, LASSO_TOLERANCE_PX).map((i) => ({ pt: path[i]!, run: [] })))
          : null;
        paintLive();
        if (!ring) {
          toast('Drag a line all the way around the ground, then let go.');
          return;
        }
        lassos.push(ring);
        actions.push({ kind: 'lasso' });
        paintStatic();
        update();
        finish();
      };
      canvas.addEventListener('pointermove', move);
      canvas.addEventListener('pointerup', up);
      canvas.addEventListener('pointercancel', up);
    };
    const startPaint = (ev: PointerEvent): void => {
      ev.preventDefault();
      ev.stopPropagation();
      canvas.setPointerCapture(ev.pointerId);
      map.dragPan.disable();
      const rect = canvas.getBoundingClientRect();
      const at = (p: PointerEvent): [number, number] => [p.clientX - rect.left, p.clientY - rect.top];
      const start = at(ev);
      const ll = map.unproject(start);
      live = { pts: [[ll.lng, ll.lat]], lat: ll.lat,
               radiusM: radiusPx * metersPerPixel(map.getZoom(), ll.lat) };
      let lastPx = start;
      placeBrush(start[0], start[1]);
      paintLive();
      const move = (m: PointerEvent): void => {
        if (m.pointerId !== ev.pointerId || !live) return;
        const p = at(m);
        placeBrush(p[0], p[1]);
        if (Math.hypot(p[0] - lastPx[0], p[1] - lastPx[1]) < 4) return;
        lastPx = p;
        const q = map.unproject(p);
        live.pts.push([q.lng, q.lat]);
        paintLive();
      };
      stopStroke = () => {
        canvas.removeEventListener('pointermove', move);
        canvas.removeEventListener('pointerup', up);
        canvas.removeEventListener('pointercancel', up);
        if (canvas.hasPointerCapture(ev.pointerId)) canvas.releasePointerCapture(ev.pointerId);
        if (wasDragPan) map.dragPan.enable();
        live = null;
        stopStroke = null;
      };
      const up = (u: PointerEvent): void => {
        if (u.pointerId !== ev.pointerId) return;
        if (u.type !== 'pointercancel') move(u);
        const s = live;
        stopStroke?.();
        if (!s || done || u.type === 'pointercancel') { paintLive(); return; }
        strokes.push({ line: s.pts, radiusM: s.radiusM, w0: strokeWidthZ0(s.radiusM, s.lat) });
        actions.push({ kind: 'stroke' });
        paintStatic();
        paintLive();
        update();
      };
      canvas.addEventListener('pointermove', move);
      canvas.addEventListener('pointerup', up);
      canvas.addEventListener('pointercancel', up);
    };

    const undo = (): void => {
      const a = actions.pop();
      if (!a) return;
      if (a.kind === 'corner') corners.pop();
      else if (a.kind === 'stroke') strokes.pop();
      else if (a.kind === 'lasso') lassos.pop();
      else if (a.added) units.delete(a.unit.id);
      else units.set(a.unit.id, a.unit);
      paintStatic();
      paintLive();
      update();
    };

    const cleanup = (): void => {
      stopStroke?.();
      bar.close();
      lines.destroy();
      release();
      map.off('click', onClick);
      map.off('mousemove', onMove);
      map.off('dblclick', onDbl);
      map.off('moveend', onMoveEnd);
      canvas.removeEventListener('pointerdown', onDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
      if (wasDoubleZoom) map.doubleClickZoom.enable();
      if (wasDragPan) map.dragPan.enable();
      canvas.style.cursor = '';
      brushEl?.remove();
      brushEl = null;
      setData(map, SRC_AREA, EMPTY);
      setData(map, SRC_AREA_LIVE, EMPTY);
    };
    const finish = (): void => {
      if (done) return;
      let ring: Pt[] | null = null;
      if (corners.length >= 3) {
        // Close the ring along the lines too, when both ends were snapped.
        const path = joined(hitFor(corners[corners.length - 1]!), hitFor(corners[0]!));
        ring = ringOf(corners, path && path.length > 2 ? path.slice(1, -1) : []);
      }
      if (corners.length && !ring) {
        toast('Complete the outline with at least three distinct corners, or undo it before previewing.');
        return;
      }
      if (!ring && !lassos.length && !strokes.length && !units.size) {
        toast('Draw around the ground, click three corners, paint a stroke, or pick a unit.');
        return;
      }
      done = true;
      const out: AreaInput = { level: lines.level() || 2, iso: opts.iso ?? '' };
      const polygon = polygonOfRings([...(ring ? [ring] : []), ...lassos].map((r) => [r]));
      if (polygon) out.polygon = polygon;
      if (strokes.length) out.strokes = strokes.map((s) => ({ line: s.line, radiusM: s.radiusM }));
      if (units.size) out.units = [...units.keys()];
      cleanup();
      resolve(out);
    };
    const cancel = (): void => {
      if (done) return;
      done = true;
      cleanup();
      resolve(null);
    };
    const onDbl = (e: maplibregl.MapMouseEvent): void => {
      if (mode !== 'draw') return;
      e.preventDefault();
      finish();
    };
    const onMoveEnd = (): void => { index = null; };
    const setSpace = (held: boolean): void => {
      if (spaceHeld === held) return;
      spaceHeld = held;
      canvas.style.cursor = held ? 'grab' : 'crosshair';
    };
    const onKey = (e: KeyboardEvent): void => {
      const typing = (document.activeElement as HTMLElement | null)?.matches?.('input, textarea, select');
      if (typing) return;
      if (e.code === 'Space') {
        // Space would also press whichever bar button has focus.
        e.preventDefault();
        if (document.activeElement instanceof HTMLButtonElement) document.activeElement.blur();
        setSpace(true);
      } else if (e.key === 'Escape') cancel();
      else if ((e.key === '[' || e.key === ']') && mode === 'paint') stepRadius(e.key === ']' ? 1 : -1, false);
    };

    const onKeyUp = (e: KeyboardEvent): void => { if (e.code === 'Space') setSpace(false); };
    const onBlur = (): void => setSpace(false);

    const bar = showDrawBar({
      surface: map.getCanvas(),
      title: opts.title,
      finishLabel: 'Preview area',
      pointShortcuts: true,
      minPoints: 1,
      minHint: 'Draw around, click corners, paint, or pick units',
      onFinish: finish,
      onUndo: undo,
      onCancel: cancel,
      extras: [
        { key: 'lasso', label: '➰ Draw around', title: 'Press and drag a line around the ground; let go to close it',
          onClick: () => setMode('lasso') },
        { key: 'draw', label: '✎ Click corners', title: 'Click the corners of the area — they can snap to lines',
          onClick: () => setMode('draw') },
        { key: 'paint', label: '◉ Paint area', title: 'Drag a brush over the ground ([ and ] resize it)',
          onClick: () => setMode('paint') },
        { key: 'units', label: '▣ Pick districts', title: 'Click admin units to add or remove them',
          onClick: () => setMode('units') },
        { key: 'snap', label: '⌁ Snap to lines', on: false, advanced: true,
          title: 'Corners snap to the lines under them — outlines, admin boundaries, roads. Alt places one freely.',
          onClick: () => { snapping = !snapping; if (!snapping) follow = false; bar.setExtra('snap', { on: snapping }); bar.setExtra('follow', { on: follow }); update(); } },
        { key: 'follow', label: '⇝ Follow lines', on: false, advanced: true,
          title: 'Two snapped corners in a row are joined along the lines between them, not by a straight edge',
          onClick: () => { follow = !follow; if (follow) snapping = true; bar.setExtra('follow', { on: follow }); bar.setExtra('snap', { on: snapping }); update(); } },
        { key: 'radius', label: `◌ ${radiusPx}px`, title: 'Brush size — also [ and ]',
          onClick: () => stepRadius(1, true) },
        { key: 'admin', advanced: true, label: '▦ Admin',
          title: 'Admin boundaries to snap to and pick units from: off, then each level the country has — detailed where loaded',
          onClick: () => { lines.cycleLevel(); } },
        { key: 'roads', advanced: true, label: '⛙ Roads', title: 'Snap to roads (OpenStreetMap, fetched for the view)',
          onClick: () => { lines.setRoads(!lines.roads()); bar.setExtra('roads', { on: lines.roads() }); } },
      ],
    });

    map.doubleClickZoom.disable();
    canvas.style.cursor = 'crosshair';
    map.on('click', onClick);
    map.on('mousemove', onMove);
    map.on('dblclick', onDbl);
    map.on('moveend', onMoveEnd);
    canvas.addEventListener('pointerdown', onDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    setMode(mode);
    paintStatic();
  });
}
